// src/rms/i18n/locale.js
// ============================================================================
// Which language the app is currently speaking
// ============================================================================
// The language list is DATA, exactly like the vertical presets: nothing in the
// app branches on a locale code. Adding a fourteenth language means adding a
// row here and a catalogue file, and nothing else.
//
// Two things live here and nowhere else:
//
//   1. the supported locales, with the endonym each one is written in
//   2. the currently active locale, plus the formatters that depend on it
//
// The active locale is module state rather than React state on purpose. Money
// and dates are formatted in ~30 places, including inside plain service
// functions that have no access to a hook -- rentalService builds invoice line
// descriptions, exportService writes CSV. Threading a locale argument through
// all of them to reach a value that changes maybe once a session would be a
// lot of noise for no benefit. Components that must re-render on a change
// subscribe; the rest simply read the current value the next time they run.
// ============================================================================

/**
 * The thirteen locales being shipped.
 *
 * `endonym` is the language's own name in its own script, because a language
 * picker that lists "Japanese" is useless to somebody who cannot yet read the
 * interface. That is the entire point of the control.
 *
 * Egyptian Arabic (arz) and Nigerian Pidgin (pcm) were deliberately left out:
 * Egyptian Arabic is a spoken variety, and formal written material in Egypt
 * uses Modern Standard Arabic, which `ar` already covers for all 26 countries;
 * Nigerian Pidgin has essentially no Intl support, so its dates, numbers and
 * plural rules would silently fall back while claiming to be localized.
 */
export const SUPPORTED_LOCALES = [
  { code: "en", name: "English", endonym: "English" },
  { code: "es", name: "Spanish", endonym: "Español" },
  { code: "fr", name: "French", endonym: "Français" },
  { code: "de", name: "German", endonym: "Deutsch" },
  { code: "pt", name: "Portuguese", endonym: "Português" },
  { code: "ru", name: "Russian", endonym: "Русский" },
  { code: "ar", name: "Arabic", endonym: "العربية", rtl: true },
  { code: "hi", name: "Hindi", endonym: "हिन्दी" },
  { code: "bn", name: "Bengali", endonym: "বাংলা" },
  { code: "ur", name: "Urdu", endonym: "اردو", rtl: true },
  { code: "mr", name: "Marathi", endonym: "मराठी" },
  { code: "id", name: "Indonesian", endonym: "Bahasa Indonesia" },
  { code: "ja", name: "Japanese", endonym: "日本語" },
];

export const DEFAULT_LOCALE = "en";

const BY_CODE = new Map(SUPPORTED_LOCALES.map((l) => [l.code, l]));

export const isSupported = (code) => BY_CODE.has(code);

/** Right-to-left scripts. Currently Arabic and Urdu. */
export const isRtl = (code) => !!BY_CODE.get(code)?.rtl;

/**
 * Best supported match for a tag, so 'pt-BR' finds 'pt' rather than falling all
 * the way back to English. Returns null when nothing matches.
 */
export function resolveLocale(tag) {
  if (!tag) return null;
  const wanted = String(tag).toLowerCase();
  if (BY_CODE.has(wanted)) return wanted;
  const base = wanted.split("-")[0];
  return BY_CODE.has(base) ? base : null;
}

/** First of the browser's preferred languages that we actually support. */
export function detectLocale() {
  const preferred = typeof navigator !== "undefined" ? navigator.languages || [navigator.language] : [];
  for (const tag of preferred) {
    const match = resolveLocale(tag);
    if (match) return match;
  }
  return DEFAULT_LOCALE;
}

// ----------------------------------------------------------------------------
// Active locale
// ----------------------------------------------------------------------------

let active = DEFAULT_LOCALE;
const listeners = new Set();

export const getLocale = () => active;

export function setLocale(code) {
  const next = resolveLocale(code) || DEFAULT_LOCALE;
  if (next === active) return active;
  active = next;
  applyDocumentLocale(next);
  listeners.forEach((fn) => fn(next));
  return next;
}

/** @returns {() => void} unsubscribe */
export function subscribeToLocale(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ----------------------------------------------------------------------------
// Remembering the choice
// ----------------------------------------------------------------------------
// Kept here rather than in RmsContext because the picker has to work on the
// landing and sign-in pages, which render outside RmsProvider entirely. A
// visitor deciding they cannot read the site is exactly the person who needs
// the control, and they have not signed in yet.

const STORED_KEY = "rms.locale";

/** The locale this person chose on a previous visit, if it is still supported. */
export function readStoredLocale() {
  try {
    return resolveLocale(window.localStorage.getItem(STORED_KEY));
  } catch {
    return null; // private browsing, or storage disabled
  }
}

/**
 * Switches language and remembers it.
 *
 * Deliberately not written to rms_operator_members.ui_locale on every change:
 * there may be no member row yet, and a click on a picker is not the same
 * commitment as saving a setting. Persisting to the account belongs on the
 * settings screen.
 */
export function chooseLocale(code) {
  const next = setLocale(code);
  try {
    window.localStorage.setItem(STORED_KEY, next);
  } catch {
    // The language still applies for this session; it just will not be
    // remembered. Not worth failing over.
  }
  return next;
}

/**
 * Mirrors the locale onto <html>. `lang` is what screen readers and search
 * engines read; `dir` is what makes the whole layout flip for Arabic and Urdu,
 * which is why the stylesheet uses logical properties throughout.
 */
export function applyDocumentLocale(code) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = code;
  document.documentElement.dir = isRtl(code) ? "rtl" : "ltr";
}

// ----------------------------------------------------------------------------
// Formatters
// ----------------------------------------------------------------------------
// Intl objects are genuinely expensive to construct, and these run inside table
// rows, so they are cached per locale + options. The cache is keyed on the
// locale too, which means switching language cannot serve a stale formatter.

const cache = new Map();

function formatter(Ctor, kind, opts) {
  const key = `${kind}:${active}:${JSON.stringify(opts || {})}`;
  let f = cache.get(key);
  if (!f) {
    f = new Ctor(active, opts);
    cache.set(key, f);
  }
  return f;
}

/** Grouped number: "1,284" in English, "١٬٢٨٤" in Arabic. */
export function formatNumber(value, opts) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return formatter(Intl.NumberFormat, "num", opts).format(n);
  } catch {
    return String(value);
  }
}

/**
 * Money from integer cents, which is the only way money is stored here.
 * Falls back to a plain rendering rather than throwing if a currency code is
 * ever wrong -- a broken total on screen is easier to diagnose than a blank
 * page.
 */
export function formatMoneyCents(cents, currency = "USD", opts) {
  const value = (Number(cents) || 0) / 100;
  try {
    return formatter(Intl.NumberFormat, "money", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
      ...opts,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

// ----------------------------------------------------------------------------
// Boot
// ----------------------------------------------------------------------------
// Resolve the active locale at module load, before anything reads getLocale().
//
// This used to happen only inside RmsProvider, which meant every page rendered
// outside it -- the landing page, the media kit, sign-in, the vertical picker --
// booted at DEFAULT_LOCALE and silently discarded a choice the visitor had
// already made. Picking Urdu and coming back to English is the precise failure
// the picker exists to prevent, and it was worst on the public pages, where a
// first-time visitor who cannot read English meets the product.
//
// It belongs here rather than in a provider because the locale is a property of
// the person, not of the RMS surface. i18n/index.js initialises i18next with
// `lng: getLocale()`, so this has to have run by then -- putting it at the end
// of this module guarantees that, since importing locale.js runs the whole file
// before any importer continues.
(function bootLocale() {
  const initial = readStoredLocale() || detectLocale();
  active = initial;
  applyDocumentLocale(initial);
})();
